import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  settingsApi,
  type ReminderAutomationSettings,
  type UpcomingDueReminderSettings,
  type CrmSyncScheduleSettings,
  type TestMsg91Request,
} from '../api/settings.api';
import { toast } from 'react-hot-toast';

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<
    'general' | 'sites' | 'automation' | 'integrations'
  >('general');

  const { data: sites } = useQuery({
    queryKey: ['settings', 'sites'],
    queryFn: settingsApi.getSites,
  });

  const { data: automationSettings, isLoading: automationLoading } = useQuery({
    queryKey: ['settings', 'reminder-automation'],
    queryFn: settingsApi.getReminderAutomation,
  });

  const { data: upcomingDueSettings, isLoading: upcomingDueLoading } = useQuery({
    queryKey: ['settings', 'upcoming-due-reminder'],
    queryFn: settingsApi.getUpcomingDueReminder,
  });

  const { data: crmSyncScheduleSettings, isLoading: crmSyncScheduleLoading } = useQuery({
    queryKey: ['settings', 'crm-sync-schedule'],
    queryFn: settingsApi.getCrmSyncSchedule,
  });

  const [automationForm, setAutomationForm] = useState<ReminderAutomationSettings>({
    enabled: false,
    frequency_days: 3,
    run_times: ['09:00', '18:00'],
    timezone: 'Asia/Kolkata',
  });
  const [upcomingDueForm, setUpcomingDueForm] = useState<UpcomingDueReminderSettings>({
    enabled: false,
    run_time: '09:00',
    days_before_due: 7,
    frequency_days: 1,
    timezone: 'Asia/Kolkata',
  });
  const [crmSyncScheduleForm, setCrmSyncScheduleForm] = useState<CrmSyncScheduleSettings>({
    enabled: true,
    run_time: '02:00',
    timezone: 'Asia/Kolkata',
  });
  const [msg91TestForm, setMsg91TestForm] = useState<TestMsg91Request>({
    phone_number: '',
    template_name: 'isp_invoice_due_reminder',
    namespace: 'dc98e981_28a0_4bb1_a080_e1c9befee582',
    payment_url: '',
    customer_name: '',
    invoice_list: '',
    total_due_amount: '',
  });

  useEffect(() => {
    if (automationSettings) {
      setAutomationForm(automationSettings);
    }
  }, [automationSettings]);

  useEffect(() => {
    if (upcomingDueSettings) {
      setUpcomingDueForm(upcomingDueSettings);
    }
  }, [upcomingDueSettings]);

  useEffect(() => {
    if (crmSyncScheduleSettings) {
      setCrmSyncScheduleForm(crmSyncScheduleSettings);
    }
  }, [crmSyncScheduleSettings]);

  const saveAutomationMutation = useMutation({
    mutationFn: settingsApi.updateReminderAutomation,
    onSuccess: () => {
      toast.success('Reminder automation settings saved');
      queryClient.invalidateQueries({ queryKey: ['settings', 'reminder-automation'] });
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: { status?: number; data?: { error?: { message?: string; details?: unknown } } };
      };
      const message = apiError.response?.data?.error?.message;
      if (apiError.response?.status === 404) {
        toast.error('Reminder automation API is unavailable. Restart the backend server and try again.');
      } else if (message) {
        toast.error(message);
      } else {
        toast.error('Failed to save reminder automation settings');
      }
    },
  });

  const saveUpcomingDueMutation = useMutation({
    mutationFn: settingsApi.updateUpcomingDueReminder,
    onSuccess: () => {
      toast.success('Upcoming due reminder settings saved');
      queryClient.invalidateQueries({ queryKey: ['settings', 'upcoming-due-reminder'] });
      queryClient.invalidateQueries({ queryKey: ['automation-jobs-status'] });
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: { status?: number; data?: { error?: { message?: string } } };
      };
      const message = apiError.response?.data?.error?.message;
      toast.error(message || 'Failed to save upcoming due reminder settings');
    },
  });

  const saveCrmSyncScheduleMutation = useMutation({
    mutationFn: settingsApi.updateCrmSyncSchedule,
    onSuccess: () => {
      toast.success('CRM sync schedule saved');
      queryClient.invalidateQueries({ queryKey: ['settings', 'crm-sync-schedule'] });
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: { status?: number; data?: { error?: { message?: string } } };
      };
      const message = apiError.response?.data?.error?.message;
      toast.error(message || 'Failed to save CRM sync schedule');
    },
  });

  const handleTestCrm = async () => {
    try {
      const result = await settingsApi.testCrmConnection();
      toast.success(result.message);
    } catch {
      toast.error('CRM connection failed');
    }
  };

  const testMsg91Mutation = useMutation({
    mutationFn: settingsApi.testMsg91Message,
    onSuccess: (data) => {
      const details = data.requestId ? ` (request: ${data.requestId})` : '';
      if (data.simulated) {
        toast(data.message, { icon: '⚠️' });
        return;
      }
      toast.success(`MSG91 test sent${details}`);
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: {
          data?: {
            error?: {
              message?: string;
              details?: string | Array<{ field?: string; message?: string }>;
            };
          };
        };
      };
      const details = apiError.response?.data?.error?.details;
      if (Array.isArray(details) && details.length > 0) {
        const first = details[0];
        toast.error(
          first.field ? `${first.field}: ${first.message || 'Invalid value'}` : first.message || 'Validation failed'
        );
        return;
      }

      const message =
        (typeof details === 'string' ? details : undefined) ||
        apiError.response?.data?.error?.message;
      toast.error(message || 'Failed to send MSG91 test message');
    },
  });

  const updateRunTime = (index: number, value: string) => {
    setAutomationForm((prev) => ({
      ...prev,
      run_times: prev.run_times.map((time, i) => (i === index ? value : time)),
    }));
  };

  const addRunTime = () => {
    setAutomationForm((prev) => ({
      ...prev,
      run_times: [...prev.run_times, '09:00'],
    }));
  };

  const removeRunTime = (index: number) => {
    setAutomationForm((prev) => ({
      ...prev,
      run_times: prev.run_times.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Configure CRM, MSG91, and reminder automation</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'general', label: 'General' },
            { id: 'sites', label: 'Sites' },
            { id: 'automation', label: 'Reminder Automation' },
            { id: 'integrations', label: 'Integrations' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">General Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Default Timezone</label>
                <select className="mt-1 block w-full border-gray-300 rounded-md shadow-sm">
                  <option>Asia/Kolkata</option>
                  <option>Asia/Dubai</option>
                  <option>UTC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <input
                  type="text"
                  value="INR"
                  disabled
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sites' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Sites</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Add Site
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CRM Site ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timezone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sites?.map((site) => (
                    <tr key={site.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {site.crm_site_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {site.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {site.timezone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            site.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {site.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'automation' && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <h2 className="text-lg font-semibold">Reminder Automation</h2>
              <p className="text-sm text-gray-500 mt-1">
                Configure the single automated engine for payment link generation and reminder
                delivery.
              </p>
            </div>

            {automationLoading ? (
              <p className="text-sm text-gray-500">Loading automation settings...</p>
            ) : (
              <>
                <div className="flex items-center justify-between border rounded-lg p-4">
                  <div>
                    <p className="font-medium text-gray-900">Enable Automated Reminder Processing</p>
                    <p className="text-sm text-gray-500">
                      At each scheduled run, finds subscribers with due/overdue invoices, ensures an
                      active payment link matches the total owed, then sends reminders every{' '}
                      {automationForm.frequency_days} day(s).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setAutomationForm((prev) => ({ ...prev, enabled: !prev.enabled }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      automationForm.enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        automationForm.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reminder Frequency (Days)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={automationForm.frequency_days}
                    onChange={(e) =>
                      setAutomationForm((prev) => ({
                        ...prev,
                        frequency_days: Math.max(1, parseInt(e.target.value, 10) || 1),
                      }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Scheduler Run Times
                    </label>
                    <button
                      type="button"
                      onClick={addRunTime}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Add time
                    </button>
                  </div>
                  <div className="space-y-2">
                    {automationForm.run_times.map((time, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="time"
                          value={time}
                          onChange={(e) => updateRunTime(index, e.target.value)}
                          className="block border border-gray-300 rounded-md shadow-sm px-3 py-2"
                        />
                        {automationForm.run_times.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRunTime(index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <input
                    type="text"
                    value={automationForm.timezone}
                    onChange={(e) =>
                      setAutomationForm((prev) => ({ ...prev, timezone: e.target.value }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => saveAutomationMutation.mutate(automationForm)}
                  disabled={saveAutomationMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveAutomationMutation.isPending ? 'Saving...' : 'Save Automation Settings'}
                </button>

                <div className="border-t border-gray-200 pt-6 mt-8 space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Upcoming Due Reminders</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Daily job for invoices due in 1–{upcomingDueForm.days_before_due} days. Generates a
                      payment link when missing, then sends email + WhatsApp every day until invoices
                      become due or overdue.
                    </p>
                  </div>

                  {upcomingDueLoading ? (
                    <p className="text-sm text-gray-500">Loading...</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Enable upcoming due reminders</p>
                          <p className="text-xs text-gray-500">Runs once daily at the configured time</p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setUpcomingDueForm((prev) => ({ ...prev, enabled: !prev.enabled }))
                          }
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            upcomingDueForm.enabled ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              upcomingDueForm.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Daily run time
                        </label>
                        <input
                          type="time"
                          value={upcomingDueForm.run_time}
                          onChange={(e) =>
                            setUpcomingDueForm((prev) => ({ ...prev, run_time: e.target.value }))
                          }
                          className="block border border-gray-300 rounded-md shadow-sm px-3 py-2"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Days before due (max window)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={30}
                          value={upcomingDueForm.days_before_due}
                          onChange={(e) =>
                            setUpcomingDueForm((prev) => ({
                              ...prev,
                              days_before_due: Math.max(
                                1,
                                Math.min(30, parseInt(e.target.value, 10) || 1)
                              ),
                            }))
                          }
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                        />
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-700">Reminder frequency</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Once per day at the scheduled run time (generates a payment link first if
                          needed).
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                        <input
                          type="text"
                          value={upcomingDueForm.timezone}
                          onChange={(e) =>
                            setUpcomingDueForm((prev) => ({ ...prev, timezone: e.target.value }))
                          }
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => saveUpcomingDueMutation.mutate(upcomingDueForm)}
                        disabled={saveUpcomingDueMutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saveUpcomingDueMutation.isPending
                          ? 'Saving...'
                          : 'Save Upcoming Due Settings'}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'integrations' && (
          <div className="space-y-6">
            <div className="border rounded-lg p-6 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">CRM Integration</h3>
                  <p className="text-sm text-gray-500">Configure CRM connection settings</p>
                </div>
                <button
                  onClick={handleTestCrm}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Test Connection
                </button>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-sm text-yellow-800">
                <strong>Note:</strong> Razorpay is owned by the CRM. This app only calls the CRM
                to fetch or generate payment links. Never directly.
              </div>

              <div className="border-t border-gray-200 pt-6 space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-gray-900">Daily CRM Sync</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Syncs sites, subscribers, and invoices from CRM once daily at the configured
                    time.
                  </p>
                </div>

                {crmSyncScheduleLoading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Automatic daily CRM sync</p>
                        <p className="text-xs text-gray-500">Runs once daily at the configured time</p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setCrmSyncScheduleForm((prev) => ({ ...prev, enabled: !prev.enabled }))
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          crmSyncScheduleForm.enabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            crmSyncScheduleForm.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Daily run time
                      </label>
                      <input
                        type="time"
                        value={crmSyncScheduleForm.run_time}
                        onChange={(e) =>
                          setCrmSyncScheduleForm((prev) => ({
                            ...prev,
                            run_time: e.target.value,
                          }))
                        }
                        className="block border border-gray-300 rounded-md shadow-sm px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                      <input
                        type="text"
                        value={crmSyncScheduleForm.timezone}
                        onChange={(e) =>
                          setCrmSyncScheduleForm((prev) => ({
                            ...prev,
                            timezone: e.target.value,
                          }))
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => saveCrmSyncScheduleMutation.mutate(crmSyncScheduleForm)}
                      disabled={saveCrmSyncScheduleMutation.isPending}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saveCrmSyncScheduleMutation.isPending
                        ? 'Saving...'
                        : 'Save CRM Sync Schedule'}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="border rounded-lg p-6 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">MSG91 WhatsApp</h3>
                  <p className="text-sm text-gray-500">
                    Send a test template message with custom parameters
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="text"
                    value={msg91TestForm.phone_number}
                    onChange={(e) =>
                      setMsg91TestForm((prev) => ({ ...prev, phone_number: e.target.value }))
                    }
                    placeholder="919876543210"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Name</label>
                  <input
                    type="text"
                    value={msg91TestForm.template_name}
                    onChange={(e) =>
                      setMsg91TestForm((prev) => ({ ...prev, template_name: e.target.value }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use the exact approved MSG91 template name (default is your due reminder template).
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Namespace</label>
                  <input
                    type="text"
                    value={msg91TestForm.namespace}
                    onChange={(e) =>
                      setMsg91TestForm((prev) => ({ ...prev, namespace: e.target.value }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Payment URL</label>
                  <input
                    type="url"
                    value={msg91TestForm.payment_url}
                    onChange={(e) =>
                      setMsg91TestForm((prev) => ({ ...prev, payment_url: e.target.value }))
                    }
                    placeholder="https://example.com/pay/abc123"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer Name</label>
                  <input
                    type="text"
                    value={msg91TestForm.customer_name}
                    onChange={(e) =>
                      setMsg91TestForm((prev) => ({ ...prev, customer_name: e.target.value }))
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Total Due Amount</label>
                  <input
                    type="text"
                    value={msg91TestForm.total_due_amount}
                    onChange={(e) =>
                      setMsg91TestForm((prev) => ({ ...prev, total_due_amount: e.target.value }))
                    }
                    placeholder="1250.00"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Invoice List</label>
                  <textarea
                    value={msg91TestForm.invoice_list}
                    onChange={(e) =>
                      setMsg91TestForm((prev) => ({ ...prev, invoice_list: e.target.value }))
                    }
                    placeholder="INV-001 | 1000 | Jun 16, 2026 (Overdue)"
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                  />
                </div>
              </div>
              <button
                onClick={() => testMsg91Mutation.mutate(msg91TestForm)}
                disabled={testMsg91Mutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {testMsg91Mutation.isPending ? 'Sending...' : 'Send Test Message'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
